import { API_HOST } from "../../config/api";
import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import useAuth from "utils/auth";

const BackgroundWrapper = styled.div`
  background-color: #f0f2f5;
  width: 100%;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
`;

const ContentWrapper = styled.div`
  max-width: 600px;
  width: 100%;
  padding: 40px;
  background: #ffffff;
  border-radius: 12px;
  color: #000;
  text-align: center;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h1`
  font-family: Pretendard, sans-serif;
  font-size: 36px;
  font-weight: 600;
  margin-bottom: 20px;
  color: #4caf50;
`;

const Description = styled.p`
  font-family: Pretendard, sans-serif;
  font-size: 18px;
  font-weight: 400;
  line-height: 27px;
  opacity: 0.8;
  margin-bottom: 30px;
`;

const Button = styled.button`
  padding: 12.5px 20px;
  border-radius: 8px;
  background-color: #ff3e61;
  font-family: Pretendard, sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: #fff;
  border: none;
  cursor: pointer;
  transition: background-color 0.3s;
  &:hover {
    background-color: #ff2545;
  }
`;

const IconWrapper = styled.div`
  margin-bottom: 20px;
`;

const OrderDetails = styled.div`
  background-color: #f9f9f9;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 30px;
  text-align: left;
`;

const OrderDetailItem = styled.p`
  font-family: Pretendard, sans-serif;
  font-size: 16px;
  margin-bottom: 10px;
  display: flex;
  justify-content: space-between;
`;

export function SuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [confirmationStatus, setConfirmationStatus] = useState("pending");
  const { refreshToken } = useAuth();
  const hasConfirmedRef = useRef(false);

  useEffect(() => {
    // 이미 confirm이 실행되었으면 다시 실행하지 않음
    if (hasConfirmedRef.current) {
      return;
    }

    // Toss Payments가 successUrl에 자동으로 추가하는 파라미터들
    const orderId = searchParams.get("orderId");
    const amount = searchParams.get("amount"); // Toss Payments에서 자동 추가
    const paymentKey = searchParams.get("paymentKey"); // Toss Payments에서 자동 추가
    
    // 필수 파라미터가 없으면 실행하지 않음
    if (!orderId || !amount || !paymentKey) {
      return;
    }
    
    // confirm 실행 플래그 설정
    hasConfirmedRef.current = true;
    
    // 백엔드에서 orderId로 저장된 amount와 days를 조회하여 검증
    const requestData = {
      orderId: orderId,
      amount: amount, // Toss Payments에서 받은 실제 결제 금액 (백엔드에서 검증)
      paymentKey: paymentKey,
      // days는 백엔드에서 orderId로 조회
    };

    async function confirm() {
      try {
        const token = localStorage.getItem("access_token");
        const headers = {
          "Content-Type": "application/json",
        };
        
        // 토큰이 있으면 Authorization 헤더 추가
        if (token) {
          headers["Authorization"] = `Bearer ${token}`;
        }

        const response = await fetch(
          `${API_HOST}/api/v1b/payments/confirm`,
          {
            method: "POST",
            headers: headers,
            body: JSON.stringify(requestData),
          }
        );
        const json = await response.json();
        if (!response.ok) {
          setConfirmationStatus("failed");
          // 에러 응답 구조: { detail: { code, message } }
          const errorCode = json.detail?.code || json.code;
          const errorMessage = json.detail?.message || json.message || "결제 처리 중 오류가 발생했습니다.";
          navigate(`/fail?message=${encodeURIComponent(errorMessage)}&code=${encodeURIComponent(errorCode || "")}`);
          return;
        }
        
        // 결제 성공 후 토큰 강제 갱신
        setTimeout(async () => {
          await refreshToken();
          window.dispatchEvent(new CustomEvent("tokenUpdated"));
        }, 1000);
        
        setConfirmationStatus("success");
      } catch (error) {
        console.error("Payment confirmation error:", error);
        setConfirmationStatus("failed");
        navigate("/fail?message=Unexpected error occurred");
      }
    }

    confirm();
  }, [navigate, searchParams, refreshToken]);

  const CheckIcon = () => (
    <svg
      width="64"
      height="64"
      viewBox="0 0 64 64"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="32" cy="32" r="32" fill="#4CAF50" />
      <path
        d="M20 32L28 40L44 24"
        stroke="white"
        strokeWidth="4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  if (confirmationStatus === "pending") {
    return (
      <BackgroundWrapper>
        <ContentWrapper>
          <Title>결제 확인 중...</Title>
          <Description>
            잠시만 기다려 주세요. 결제를 확인하고 있습니다.
          </Description>
        </ContentWrapper>
      </BackgroundWrapper>
    );
  }

  if (confirmationStatus === "failed") {
    return null; // 실패 시 navigate('/fail')에 의해 다른 페이지로 이동됩니다.
  }

  return (
    <BackgroundWrapper>
      <ContentWrapper>
        <IconWrapper>
          <CheckIcon />
        </IconWrapper>
        <Title>결제가 완료되었습니다!</Title>
        <Description>
          저희 서비스를 이용해 주셔서 감사합니다. 이제 모든 기능을 자유롭게
          사용하실 수 있습니다. 상위 1% 트레이더들의 통찰력 있는 전략을
          만나보세요.
        </Description>
        <OrderDetails>
          <OrderDetailItem>
            <strong>주문번호:</strong> {searchParams.get("orderId")}
          </OrderDetailItem>
          <OrderDetailItem>
            <strong>결제 금액:</strong>{" "}
            {Number(searchParams.get("amount")).toLocaleString()}원
          </OrderDetailItem>
          <OrderDetailItem>
            <strong>결제 키:</strong> {searchParams.get("paymentKey")}
          </OrderDetailItem>
        </OrderDetails>
        <Button onClick={() => navigate("/trader")}>
          트레이더 분석 시작하기
        </Button>
      </ContentWrapper>
    </BackgroundWrapper>
  );
}

export default SuccessPage;
